// src/router.js
import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, refreshUserToken } from '@/firebase';

const commonFinanceClaims = ['superadmin', 'operations', 'accounts', 'finance'];
const financialClaims = ['superadmin', 'accounts', 'finance'];
const payrollClaims = ['superadmin', 'operations', 'accounts'];
const annualClaims = ['superadmin', 'accounts'];
const tenantClaims = ['superadmin', 'accounts'];

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginPage.vue'),
    meta: { title: 'Login - IMAB Finance', noLayout: true }
  },
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('@/views/HomePage.vue'),
    meta: { title: 'Dashboard - IMAB Finance', requiresAuth: true, allowedClaims: commonFinanceClaims }
  },
  {
    path: '/employees',
    name: 'Employees',
    component: () => import('@/views/EmployeesPage.vue'),
    meta: { title: 'Employees - IMAB Finance', requiresAuth: true, allowedClaims: commonFinanceClaims },
  },
  {
    path: '/batches',
    name: 'Batches',
    component: () => import('@/views/BatchesPage.vue'),
    meta: { title: 'Batches - IMAB Finance', requiresAuth: true, allowedClaims: payrollClaims },
  },
  {
    path: '/batches/generate/:batchName',
    name: 'PayrollGenerate',
    component: () => import('@/views/PayrollGenerate.vue'),
    meta: { title: 'Preview Batch - IMAB Finance', requiresAuth: true, allowedClaims: payrollClaims },
    props: true
  },
  {
    path: '/batches/view/:batchName',
    name: 'PayrollViewPage',
    component: () => import('@/views/PayrollViewPage.vue'),
    meta: { title: 'View Batch - IMAB Finance', requiresAuth: true, allowedClaims: payrollClaims },
    props: true
  },
  {
    path: '/structure',
    name: 'Structure',
    component: () => import('@/views/StructurePage.vue'),
    meta: { title: 'Structure - IMAB Finance', requiresAuth: true, allowedClaims: payrollClaims },
  },
  {
    path: '/annuals',
    name: 'Annuals',
    component: () => import('@/views/AnnualsPage.vue'),
    meta: { title: 'Annuals - IMAB Finance', requiresAuth: true, allowedClaims: annualClaims },
  },
  {
    path: '/annuals/esps/:employeeId',
    name: 'EmployeeEspAnnuals',
    component: () => import('@/views/EmployeeEspAnnualsPage.vue'),
    meta: { title: 'Employee ESP Annuals - IMAB Finance', requiresAuth: true, allowedClaims: annualClaims },
    props: true,
  },
  {
    path: '/annuals/:batchName',
    name: 'AnnualsInnerpage',
    component: () => import('@/views/AnnualsViewPage.vue'),
    meta: { title: 'Preview Batch Annuals - IMAB Finance', requiresAuth: true, allowedClaims: annualClaims },
    props: true
  },
  {
    path: '/annuals/:batchName/:annualsType/:employeeId',
    name: 'AnnualsEmployeesPage',
    component: () => import('@/views/AnnualsEmployeesPage.vue'),
    meta: { title: 'Preview Employee Annuals - IMAB Finance', requiresAuth: true, allowedClaims: annualClaims },
    props: true
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/ProfilePage.vue'),
    meta: { title: 'Profile - IMAB Finance', requiresAuth: true }
  },
  {
    path: '/tenants',
    name: 'Tenants',
    component: () => import('@/tenants/views/AllTenants.vue'),
    meta: { title: 'Tenants - IMAB Finance', requiresAuth: true, allowedClaims: tenantClaims }
  },
  {
    path: '/invoices',
    name: 'Invoices',
    component: () => import('@/views/InvoicePage.vue'),
    meta: { title: 'Invoices - IMAB Finance', requiresAuth: true, allowedClaims: commonFinanceClaims },
  },
  {
    path: '/invoices/create/:batch_name/:company_id',
    name: 'Create Invoice',
    component: () => import('@/views/InvoiceCreatePage.vue'),
    meta: { title: 'Create Invoice - IMAB Finance', requiresAuth: true, allowedClaims: commonFinanceClaims },
    props: true
  },
  {
    path: '/invoices/payment-profiles',
    name: 'Payment Profiles',
    component: () => import('@/views/PaymentProfilesPage.vue'),
    meta: { title: 'Payment Profiles - IMAB Finance', requiresAuth: true, allowedClaims: financialClaims },
  },
  {
    path: '/invoices/:batch',
    redirect: to => `/invoices/${to.params.batch}/all`,
  },
  {
    path: '/invoices/:batch/:scope(all|UK|PK)',
    name: 'Invoice Batch',
    component: () => import('@/views/InvoiceBatchPage.vue'),
    meta: { title: 'Invoices Batch - IMAB Finance', requiresAuth: true, allowedClaims: commonFinanceClaims },
    props: true
  },
  {
    path: '/invoices/:batch/:invoiceId(\\d+)',
    name: 'Invoice Detail',
    component: () => import('@/views/InvoiceDetailPage.vue'),
    meta: { title: 'Invoice Detail - IMAB Finance', requiresAuth: true, allowedClaims: commonFinanceClaims },
    props: true
  },
  {
    path: '/companies',
    name: 'Companies',
    component: () => import('@/views/CompaniesPage.vue'),
    meta: { title: 'Companies - IMAB Finance', requiresAuth: true, allowedClaims: commonFinanceClaims },
  },
  {
    path: '/expenses',
    name: 'Expenses',
    component: () => import('@/views/ExpensesPage.vue'),
    meta: { title: 'Expenses - IMAB Finance', requiresAuth: true, allowedClaims: financialClaims },
  },
  {
    path: '/expenses/settings',
    name: 'Expense Settings',
    component: () => import('@/views/ExpenseSettingsPage.vue'),
    meta: { title: 'Expense Settings - IMAB Finance', requiresAuth: true, allowedClaims: financialClaims },
  },
  {
    path: '/expenses/source-payments',
    name: 'Source Payments',
    component: () => import('@/views/SourcePaymentsPage.vue'),
    meta: { title: 'Source Payments - IMAB Finance', requiresAuth: true, allowedClaims: financialClaims },
  },
  {
    path: '/expenses/:periodId',
    name: 'Expense Period',
    component: () => import('@/views/ExpensePeriodPage.vue'),
    meta: { title: 'Monthly Expenses - IMAB Finance', requiresAuth: true, allowedClaims: financialClaims },
    props: true,
  },
  {
    path: '/expenses/:periodId/statement-import/:statementId(\\d+)?',
    name: 'Statement Import',
    component: () => import('@/views/StatementImportPage.vue'),
    meta: { title: 'Import Statement - IMAB Finance', requiresAuth: true, allowedClaims: financialClaims },
  },
  {
    path: '/expenses/:periodId/hourly-expense',
    name: 'Hourly Expense Breakdown',
    component: () => import('@/views/HourlyExpensePage.vue'),
    meta: { title: 'Hourly Expense Breakdown - IMAB Finance', requiresAuth: true, allowedClaims: financialClaims },
    props: true,
  },
  {
    path: '/expenses/:periodId/groups/:groupKey',
    name: 'Expense Group',
    component: () => import('@/views/ExpenseGroupPage.vue'),
    meta: { title: 'Expense Category - IMAB Finance', requiresAuth: true, allowedClaims: financialClaims },
    props: true,
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

router.beforeEach(async (to, from, next) => {
  // Set page title if defined
  if (to.meta.title) {
    document.title = to.meta.title;
  }

  // Check if route requires auth
  if (to.meta.requiresAuth) {
    try {
      // Check if token needs refresh
      if (store.getters.shouldRefreshToken && store.getters.isAuthenticated) {
        await store.dispatch('refreshToken');
      }

      const isAuthenticated = auth.currentUser && store.state.isAuthenticated;
      
      if (!isAuthenticated) {
        const storedToken = localStorage.getItem('auth_token');
        if (storedToken) {
          try {
            const user = await new Promise((resolve) => {
              const unsubscribe = onAuthStateChanged(auth, (user) => {
                unsubscribe();
                resolve(user);
              });
            });
            
            if (user) {
              const idToken = await refreshUserToken();
              await store.dispatch('login', { user, idToken });
            } else {
              next({ name: 'Login' });
              return;
            }
          } catch (error) {
            console.error('Auth check failed:', error);
            next({ name: 'Login' });
            return;
          }
        } else {
          next({ name: 'Login' });
          return;
        }
      }
    } catch (error) {
      console.error('Router auth check failed:', error);
      next({ name: 'Login' });
      return;
    }
  }

  if (to.meta.allowedClaims) {
    const claim = store.state.user?.customClaims?.claim;
    if (!to.meta.allowedClaims.includes(claim)) {
      next({ name: 'Profile' });
      return;
    }
  }
  
  // If we reach here, either the route doesn't require auth or we're authenticated
  next();
});

export default router;
